/**
 * Agent SSE stream — the single implementation of the /api/llm-agent
 * protocol.
 *
 * Until 2026-09-21 this lived twice inside Chat.jsx (once in
 * `sendMessageWithText`, once in `regenerateResponse`) as two byte-identical
 * ~340-line blocks that differed only in a variable name and a log string.
 * Every protocol change had to be made twice, and the thinking-trace fixes
 * below would have doubled again.
 *
 * Event vocabulary (producer: rag_agent/react/streaming.py):
 *   thinking                   one step of the agent's work, `phase`-tagged
 *   observation                what that step returned (counts in metadata)
 *   answer_generation_started  retrieval done, token streaming begins
 *   answer_delta / stream      answer tokens
 *   section_open/_delta/_close per-document answer sections
 *   sources / citation_delta   reference resolution during streaming
 *   message                    final answer (authoritative)
 *   clarification / error      terminal states
 */

import { capture } from "../../../utils/analytics";

// Phase vocabulary — mirrors rag_agent/react/labels.py. Unknown phases
// fall back to the neutral gear, so a new backend phase degrades to a
// generic step instead of an empty cell.
export const PHASE_ICONS = {
  understand: "🔎",
  docs: "📄",
  sections: "📑",
  chunks: "🔍",
  tabular: "📊",
  errors: "⚠️",
  balance: "⚖️",
  answer: "✍️",
  verify: "✅",
  processing: "⚙️",
  // Legacy phase from traces restored out of localStorage, recorded
  // before the vocabulary in labels.py existed.
  routing: "🔎",
};

export const phaseIcon = (phase) => PHASE_ICONS[phase] || PHASE_ICONS.processing;

/**
 * "1,4 s" — step durations are sub-minute by construction.
 *
 * Sub-50ms steps get no duration: "0,0 s" is noise. Everything above
 * that is shown, because the point of the numbers is to make the one
 * step that costs seconds stand out against the ones that cost
 * milliseconds.
 */
export const formatDuration = (ms) => {
  if (typeof ms !== "number" || !isFinite(ms) || ms < 50) return "";
  if (ms > 10 * 60 * 1000) return "";   // clock skew, not a duration
  return `${(ms / 1000).toFixed(1).replace(".", ",")} s`;
};

/**
 * When the step happened, on the SERVER's clock.
 *
 * Arrival time is the wrong measure: SSE events coalesce in transit
 * (kernel/proxy buffering), so two steps 800 ms apart on the server can
 * reach the browser in the same read and appear to take 0 ms. Every
 * StreamEvent carries `timestamp` (streaming.py), and durations are
 * always differences between two of those, so the server's timezone
 * cancels out. Arrival time stays as the fallback.
 */
const eventTime = (data) => {
  const t = Date.parse((data && data.timestamp) || "");
  return Number.isFinite(t) ? t : Date.now();
};

// Narrative-mode answers stream a section with heading="" (deliberate
// signal — see ANSWER_SYSTEM_PROMPT "NARRATIVE-MODUS"). A naïve
// `data.section_heading || 'Dokument'` substitutes JS-falsy "" with
// "Dokument", producing a ghost <h3> during streaming that disappears at
// the final `message` event. Preserve the empty string.
const coerceHeading = (raw) => (typeof raw === "string" ? raw : "Dokument");

const extractFileName = (url) => {
  if (!url) return "";
  try {
    return decodeURIComponent(url.split("/").pop() || "");
  } catch {
    return url.split("/").pop() || "";
  }
};

/**
 * Close the step that is currently running.
 *
 * A step is "running" from its `thinking` event until either its
 * `observation` lands or the next step starts — that span is what the UI
 * shows as its duration and what the ⏳ marker points at.
 */
const closeLastStep = (steps, at) => {
  if (!Array.isArray(steps) || steps.length === 0) return steps;
  const last = steps[steps.length - 1];
  if (last.doneAt) return steps;
  return [...steps.slice(0, -1), { ...last, doneAt: at }];
};

/**
 * Run one agent query and fold its SSE events into the message list.
 *
 * @param {object}   opts
 * @param {string}   opts.term        the user's question
 * @param {string}   opts.sessionId   conversation-memory id
 * @param {Function} opts.setMessages React state setter for the message list
 * @param {string}   opts.context     "send" | "regenerate" (logging only)
 */
export async function streamAgentAnswer({ term, sessionId, setMessages, context = "send" }) {
  try {
    const resp = await fetch("/api/llm-agent", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        term,
        password: "Allrecobot6039",
        session_id: sessionId,
      }),
    });
    if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

    /* placeholder bot bubble */
    let botText = "";
    const botMessageId = Date.now() + 1;
    setMessages((prev) => [...prev, {
      id: botMessageId,
      text: "",
      from: "bot",
      timestamp: new Date(),
      reactions: [],
      citations: [],
      status: "thinking",
      tokens: 0,
      progressSteps: [],   // the thinking trace, one entry per agent step
    }]);

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let sseBuffer = ""; // Buffer for incomplete SSE chunks

    // Mutate the in-flight bot message. Every branch below needs the same
    // "is this still my message?" guard, so it lives here once.
    const updateBot = (fn) => {
      setMessages((prev) => {
        const newMessages = [...prev];
        const lastIndex = newMessages.length - 1;
        const botMessage = newMessages[lastIndex];
        if (!botMessage || botMessage.id !== botMessageId) return prev;
        const patch = fn(botMessage);
        if (!patch) return prev;
        newMessages[lastIndex] = { ...botMessage, ...patch };
        return newMessages;
      });
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      sseBuffer += chunk;

      // Only process complete lines (ending with \n)
      const lastNewlineIndex = sseBuffer.lastIndexOf("\n");
      if (lastNewlineIndex === -1) continue; // No complete lines yet

      const completeData = sseBuffer.substring(0, lastNewlineIndex);
      sseBuffer = sseBuffer.substring(lastNewlineIndex + 1); // Keep incomplete part

      const lines = completeData.split("\n");

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        try {
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;            // Skip empty data lines
          if (jsonStr === "[DONE]") break;   // Standard SSE completion signal

          const data = JSON.parse(jsonStr);
          if (!data || typeof data !== "object") continue;

          if (data.type === "stream" && data.content) {
            botText += data.content;
            updateBot(() => ({ text: botText }));

          } else if (data.type === "answer_delta" && data.content) {
            botText += data.content;
            updateBot(() => ({ text: botText, status: "streaming" }));

          } else if (data.type === "short_answer_partial" && typeof data.content === "string") {
            // RAG_SHORT_ANSWER_FIRST: whole short answer so far (a re-roll replaces it).
            updateBot(() => ({ shortAnswer: data.content, status: "streaming" }));

          } else if (data.type === "complete") {
            updateBot(() => ({ text: data.content }));

          } else if (data.type === "sources" && Array.isArray(data.sources)) {
            // Backend emits this once after retrieval, before answer streaming.
            // Lets placeholder pills upgrade to clickable as soon as their
            // citation arrives, without waiting for the final message event.
            updateBot(() => ({ sources: data.sources }));

          } else if (data.type === "citation_delta" && data.citation) {
            // One per citation as the answer-gen JSON parses incrementally.
            updateBot((bot) => {
              const existing = bot.citations || [];
              const dedup = existing.some(c => Number(c.id) === Number(data.citation.id));
              return { citations: dedup ? existing : [...existing, data.citation] };
            });

          } else if (data.type === "section_open" && typeof data.section_idx === "number") {
            // New per-doc answer section begins — push a placeholder so the
            // bubble renders the heading immediately; section_delta fills it.
            updateBot((bot) => {
              const sections = Array.isArray(bot.answerSections) ? [...bot.answerSections] : [];
              sections[data.section_idx] = {
                docId: data.section_docId || "",
                heading: coerceHeading(data.section_heading),
                content: "",
                citation_ids: [],
              };
              return { answerSections: sections, status: "streaming" };
            });

          } else if (data.type === "section_delta" && typeof data.section_idx === "number") {
            updateBot((bot) => {
              const sections = Array.isArray(bot.answerSections) ? [...bot.answerSections] : [];
              const idx = data.section_idx;
              if (!sections[idx]) return null;
              sections[idx] = {
                ...sections[idx],
                content: (sections[idx].content || "") + (data.content || ""),
              };
              return { answerSections: sections };
            });

          } else if (data.type === "section_close" && typeof data.section_idx === "number") {
            // Section finalised — scope its citations for rendering.
            updateBot((bot) => {
              const sections = Array.isArray(bot.answerSections) ? [...bot.answerSections] : [];
              const idx = data.section_idx;
              if (!sections[idx]) return null;
              sections[idx] = {
                ...sections[idx],
                citation_ids: Array.isArray(data.section_citation_ids)
                  ? data.section_citation_ids
                  : [],
              };
              return { answerSections: sections };
            });

          } else if (data.type === "message") {
            capture("answer_completed", {
              session_id: sessionId,
              n_sources: Array.isArray(data.sources) ? data.sources.length : 0,
              n_citations: Array.isArray(data.citations) ? data.citations.length : 0,
            });
            const at = eventTime(data);
            updateBot((bot) => ({
              text: data.content,
              status: "sent",
              sources: Array.isArray(data.sources) ? data.sources : bot.sources,
              // Prefer streamed citations if citation_delta events already
              // arrived; otherwise use the bulk array.
              citations: Array.isArray(data.citations) && data.citations.length > 0
                ? data.citations
                : (bot.citations || []),
              answerSections: Array.isArray(data.answer_sections) ? data.answer_sections : null,
              // RAG_SHORT_ANSWER: 1–3 sentence summary shown before the folded full answer.
              // The final value decides: a short answer the checks dropped clears the streamed one.
              shortAnswer: typeof data.short_answer === "string" ? data.short_answer : null,
              progressSteps: closeLastStep(bot.progressSteps, at),
            }));

          } else if (data.type === "thinking") {
            // One step of the agent's work. `phase` drives the icon,
            // metadata.step/of drive the progress counter — both come from
            // the shared vocabulary in rag_agent/react/labels.py, so the
            // ReAct loop and the deterministic plan executor look alike.
            const at = eventTime(data);
            const meta = data.metadata || {};
            updateBot((bot) => ({
              progressSteps: [
                ...closeLastStep(bot.progressSteps, at),
                {
                  text: data.content,
                  phase: data.phase || "processing",
                  at,
                  step: typeof meta.step === "number" ? meta.step : null,
                  of: typeof meta.of === "number" ? meta.of : null,
                },
              ],
            }));

          } else if (data.type === "observation") {
            // What the step that just ran returned, attached to that step
            // instead of becoming a line of its own.
            //
            // `content` is the observation the AGENT reads — on the ReAct
            // path it contains raw docId lists and instructions to the
            // LLM. Only `metadata.ui` is meant for humans, so a backend
            // that sends no `ui` contributes a timestamp and nothing else.
            const at = eventTime(data);
            const ui = (data.metadata || {}).ui || "";
            updateBot((bot) => {
              const steps = bot.progressSteps || [];
              if (steps.length === 0) return null;
              const last = steps[steps.length - 1];
              return {
                progressSteps: [
                  ...steps.slice(0, -1),
                  { ...last, result: ui || last.result || "", doneAt: last.doneAt || at },
                ],
              };
            });

          } else if (data.type === "answer_generation_started") {
            // Retrieval finished — LLM token streaming begins now. Flip the
            // status so the header reads "Antwort wird formuliert…".
            updateBot(() => ({ status: "generating_answer" }));

          } else if (data.type === "hierarchical_match") {
            const fileName = extractFileName(data.content.path);
            const linkHtml = `<a href="${data.content.path}" target="_blank" rel="noopener noreferrer">${fileName}</a>`;
            botText += linkHtml;
            const fileNameWithoutExt = fileName.replace(/\.[^/.]+$/, "");
            botText += `<div class="document-citation">📄 <strong>Zitiert aus:</strong> ${fileNameWithoutExt}</div>`;
            updateBot((bot) => ({
              text: botText,
              citations: [...(bot.citations || []), fileNameWithoutExt],
            }));

          } else if (data.type === "clarification") {
            // Clarification request — question plus clickable options.
            const at = eventTime(data);
            updateBot((bot) => ({
              text: data.question,
              status: "sent",
              isClarification: true,
              clarificationOptions: data.options || [],
              progressSteps: closeLastStep(bot.progressSteps, at),
            }));

          } else if (data.type === "error") {
            setMessages((prev) => [
              ...prev,
              {
                id: Date.now(),
                text: `Fehler: ${data.content}`,
                from: "bot",
                timestamp: new Date(),
                reactions: [],
              },
            ]);
          }
        } catch (e) {
          // Only log if it's not an empty message (common in SSE)
          const jsonStr = line.slice(6).trim();
          if (jsonStr) {
            console.error("Error parsing SSE data:", { error: e.message, line: jsonStr.substring(0, 100) });
          }
        }
      }
    }
  } catch (error) {
    console.error(context === "regenerate" ? "Error regenerating message:" : "Error sending message:", error);
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        text: "Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.",
        from: "bot",
        timestamp: new Date(),
        reactions: [],
      },
    ]);
  }
}
